// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { Alert, AlertTitle, Card, Radio, Typography } from '@mui/material';
import { Controller } from 'react-hook-form';
import {
  Input,
  Label,
  OptionsSelector,
  DateInput,
} from '@shared/ui-kit/components';
import { Translation } from '@shared/ui-kit/providers';
import { defineComponent } from '@shared/ui-kit/utils/define-component';
import { classNames } from '@shared/ui-kit/utils';
import {
  NavigationControls,
  ScreenHeader,
  ScreenLayout,
} from '@shared/advisory/account-workflow/layout';
import { useLegalDeclarations } from './use-legal-declarations';

export const LegalDeclarations = defineComponent({
  displayName: 'AccountOpeningLegalDeclarations',
  useSetup: useLegalDeclarations,
  View: ({
    t,
    form,
    onNext,
    isFormValid,
    isUpdatingClientOnboardingProfile,
    legalDeclarations,
    onUsTaxResidenceChange,
    onTaxResidenceDocumentNumberChange,
    taxResidenceCountryOptions,
    yesNoOptions,
    investmentOwnershipOptions,
    relationshipOptions,
  }) => {
    return (
      <ScreenLayout
        actions={
          <NavigationControls
            form="legal-declarations-form"
            isFormValid={isFormValid}
            loading={isUpdatingClientOnboardingProfile}
          />
        }
      >
        <div className="w-full">
          <form id="legal-declarations-form" onSubmit={form.handleSubmit(onNext)}>
            <fieldset className="m-0 flex min-w-0 flex-col gap-7 border-0 p-0">
              <ScreenHeader
                title={
                  <Translation
                    i18nKey="account-opening.screens.legal-declarations.title"
                    ns="components"
                  />
                }
                subtitle={
                  <Translation
                    i18nKey="account-opening.screens.legal-declarations.subtitle"
                    ns="components"
                  />
                }
              />

              <Alert
                severity="info"
                className="!rounded-lg !bg-alert-info-bg text-alert-info-text"
                data-testid="legal-declarations-alert"
              >
                <AlertTitle>
                  <Translation
                    i18nKey="account-opening.legal-declarations.alert.title"
                    ns="components"
                  />
                </AlertTitle>

                <p>
                  <Translation
                    i18nKey="account-opening.legal-declarations.alert.description"
                    ns="components"
                  />
                </p>
              </Alert>

              <Card className="!rounded-xl !p-6" variant="outlined">
                <Controller
                  control={form.control}
                  name="isPoliticallyExposedPerson"
                  render={({ field }) => (
                    <fieldset className="col-span-1 grid gap-2">
                      <Typography
                        variant="h5"
                        className="!mb-6 !text-h5 !font-semibold !text-title-navy"
                      >
                        <Translation
                          i18nKey="account-opening.legal-declarations.pep.title"
                          ns="components"
                        />
                      </Typography>

                      <Label
                        component="legend"
                        required
                        className="mb-4 !text-base !font-normal !text-title-navy"
                      >
                        <Translation
                          i18nKey="account-opening.legal-declarations.pep.question"
                          ns="components"
                        />
                      </Label>

                      <div className="flex flex-col gap-3">
                        {yesNoOptions.map(option => {
                          const isSelected = field.value === option.value;

                          return (
                            <button
                              key={String(option.value)}
                              type="button"
                              onClick={() => field.onChange(option.value)}
                              className={classNames(
                                'flex w-full items-center gap-3 rounded-2xl border border-border-light bg-white px-4 py-3 text-left transition-all hover:border-primary hover:bg-primary/5',
                                {
                                  'border-primary bg-primary/5 ring-1 ring-primary/20':
                                    isSelected,
                                },
                              )}
                              data-testid={`pep-option-${option.value}`}
                            >
                              <Radio
                                checked={isSelected}
                                className="!p-0 !text-primary hover:!bg-transparent"
                              />

                              <Typography
                                variant="body1"
                                className="!text-sm !font-normal !text-title-navy sm:!text-base"
                              >
                                <Translation
                                  i18nKey={option.label as any}
                                  ns="components"
                                />
                              </Typography>
                            </button>
                          );
                        })}
                      </div>

                      {field.value && (
                        <Card
                          className="!mt-5 !rounded-xl !bg-page !p-4"
                          variant="outlined"
                        >
                          <div className="flex flex-col gap-4">
                            <Typography
                              variant="body2"
                              className="!text-subtitle"
                              data-testid="pep-detail-hint"
                            >
                              <Translation
                                i18nKey="account-opening.legal-declarations.pep.detail.hint"
                                ns="components"
                              />
                            </Typography>

                            <fieldset className="col-span-1 grid gap-2">
                              <Label htmlFor="pep-position-input" required>
                                <Translation
                                  i18nKey="account-opening.legal-declarations.pep.detail.position"
                                  ns="components"
                                />
                              </Label>

                              <Controller
                                name="pepDetail.position"
                                control={form.control}
                                render={({ field, fieldState }) => (
                                  <Input
                                    id="pep-position-input"
                                    value={field.value ?? ''}
                                    onChange={field.onChange}
                                    placeholder={t(
                                      'account-opening.professional-profile.employed-detail.position-placeholder',
                                      { ns: 'components' },
                                    )}
                                    data-testid="pep-position-input"
                                    error={!!fieldState.error}
                                  />
                                )}
                              />
                            </fieldset>

                            <fieldset className="col-span-1 grid gap-2">
                              <Label htmlFor="pep-institution-input" required>
                                <Translation
                                  i18nKey="account-opening.legal-declarations.pep.detail.institution"
                                  ns="components"
                                />
                              </Label>

                              <Controller
                                name="pepDetail.institution"
                                control={form.control}
                                render={({ field, fieldState }) => (
                                  <Input
                                    id="pep-institution-input"
                                    value={field.value ?? ''}
                                    onChange={field.onChange}
                                    placeholder={t(
                                      'account-opening.professional-profile.employed-detail.company-name-placeholder',
                                      { ns: 'components' },
                                    )}
                                    data-testid="pep-institution-input"
                                    error={!!fieldState.error}
                                  />
                                )}
                              />
                            </fieldset>

                            <fieldset className="col-span-1 grid gap-2">
                              <Label htmlFor="pep-end-date-input">
                                <Translation
                                  i18nKey="account-opening.legal-declarations.pep.detail.end-date"
                                  ns="components"
                                />
                              </Label>

                              <Controller
                                name="pepDetail.endDate"
                                control={form.control}
                                render={({ field, fieldState }) => (
                                  <DateInput
                                    id="pep-end-date-input"
                                    name={field.name}
                                    value={field.value}
                                    onChange={field.onChange}
                                    onBlur={field.onBlur}
                                    ref={field.ref}
                                    data-testid="pep-end-date-input"
                                    error={!!fieldState.error}
                                  />
                                )}
                              />
                            </fieldset>

                            <fieldset className="col-span-1 grid gap-2">
                              <Label component="legend" required className="mb-4">
                                <Translation
                                  i18nKey="account-opening.legal-declarations.pep.detail.additional-income-question"
                                  ns="components"
                                />
                              </Label>

                              <Controller
                                name="pepDetail.hasRelatedAdditionalIncome"
                                control={form.control}
                                render={({ field }) => (
                                  <div className="flex flex-col gap-3">
                                    {yesNoOptions.map(option => {
                                      const isSelected =
                                        field.value === option.value;

                                      return (
                                        <button
                                          key={String(option.value)}
                                          type="button"
                                          onClick={() =>
                                            field.onChange(option.value)
                                          }
                                          className={classNames(
                                            'flex w-full items-center gap-3 rounded-2xl border border-border-light bg-white px-4 py-3 text-left transition-all hover:border-primary hover:bg-primary/5',
                                            {
                                              'border-primary bg-primary/5 ring-1 ring-primary/20':
                                                isSelected,
                                            },
                                          )}
                                          data-testid={`pep-additional-income-option-${option.value}`}
                                        >
                                          <Radio
                                            checked={isSelected}
                                            className="!p-0 !text-primary hover:!bg-transparent"
                                          />

                                          <Typography
                                            variant="body1"
                                            className="!text-sm !font-normal !text-title-navy sm:!text-base"
                                          >
                                            <Translation
                                              i18nKey={option.label as any}
                                              ns="components"
                                            />
                                          </Typography>
                                        </button>
                                      );
                                    })}
                                  </div>
                                )}
                              />
                            </fieldset>

                            {legalDeclarations.pepDetail
                              ?.hasRelatedAdditionalIncome && (
                              <fieldset className="col-span-1 grid gap-2">
                                <Label
                                  htmlFor="pep-additional-income-detail-input"
                                  required
                                >
                                  <Translation
                                    i18nKey="account-opening.legal-declarations.pep.detail.additional-income-detail"
                                    ns="components"
                                  />
                                </Label>

                                <Controller
                                  name="pepDetail.additionalIncomeDetail"
                                  control={form.control}
                                  render={({ field, fieldState }) => (
                                    <Input
                                      id="pep-additional-income-detail-input"
                                      value={field.value ?? ''}
                                      onChange={field.onChange}
                                      placeholder={t(
                                        'account-opening.legal-declarations.pep.detail.additional-income-placeholder',
                                        { ns: 'components' },
                                      )}
                                      data-testid="pep-additional-income-detail-input"
                                      error={!!fieldState.error}
                                    />
                                  )}
                                />
                              </fieldset>
                            )}
                          </div>
                        </Card>
                      )}
                    </fieldset>
                  )}
                />
              </Card>

              <Card className="!rounded-xl !p-6" variant="outlined">
                <Controller
                  control={form.control}
                  name="isRelatedToPublicPerson"
                  render={({ field }) => (
                    <fieldset className="col-span-1 grid gap-2">
                      <Typography
                        variant="h5"
                        className="!mb-6 !text-h5 !font-semibold !text-title-navy"
                      >
                        <Translation
                          i18nKey="account-opening.legal-declarations.public-person-relationship.title"
                          ns="components"
                        />
                      </Typography>

                      <Label
                        component="legend"
                        required
                        className="mb-4 !text-base !font-normal !text-title-navy"
                      >
                        <Translation
                          i18nKey="account-opening.legal-declarations.public-person-relationship.question"
                          ns="components"
                        />
                      </Label>

                      <div className="flex flex-col gap-3">
                        {yesNoOptions.map(option => {
                          const isSelected = field.value === option.value;

                          return (
                            <button
                              key={String(option.value)}
                              type="button"
                              onClick={() => field.onChange(option.value)}
                              className={classNames(
                                'flex w-full items-center gap-3 rounded-2xl border border-border-light bg-white px-4 py-3 text-left transition-all hover:border-primary hover:bg-primary/5',
                                {
                                  'border-primary bg-primary/5 ring-1 ring-primary/20':
                                    isSelected,
                                },
                              )}
                              data-testid={`public-person-relationship-option-${option.value}`}
                            >
                              <Radio
                                checked={isSelected}
                                className="!p-0 !text-primary hover:!bg-transparent"
                              />

                              <Typography
                                variant="body1"
                                className="!text-sm !font-normal !text-title-navy sm:!text-base"
                              >
                                <Translation
                                  i18nKey={option.label as any}
                                  ns="components"
                                />
                              </Typography>
                            </button>
                          );
                        })}
                      </div>

                      {field.value && (
                        <Card
                          className="!mt-5 !rounded-xl !bg-page !p-4"
                          variant="outlined"
                        >
                          <div className="flex flex-col gap-4">
                            <fieldset className="col-span-1 grid gap-2">
                              <Label htmlFor="public-person-name-input" required>
                                <Translation
                                  i18nKey="account-opening.legal-declarations.public-person-relationship.detail.person-name"
                                  ns="components"
                                />
                              </Label>

                              <Controller
                                name="publicPersonRelationshipDetail.personName"
                                control={form.control}
                                render={({ field, fieldState }) => (
                                  <Input
                                    id="public-person-name-input"
                                    value={field.value ?? ''}
                                    onChange={field.onChange}
                                    placeholder={t(
                                      'account-opening.legal-declarations.public-person-relationship.detail.person-name-placeholder',
                                      { ns: 'components' },
                                    )}
                                    data-testid="public-person-name-input"
                                    error={!!fieldState.error}
                                  />
                                )}
                              />
                            </fieldset>

                            <fieldset className="col-span-1 grid gap-2">
                              <Label
                                htmlFor="public-person-relationship-selector"
                                required
                              >
                                <Translation
                                  i18nKey="account-opening.legal-declarations.public-person-relationship.detail.relationship.label"
                                  ns="components"
                                />
                              </Label>

                              <Controller
                                name="publicPersonRelationshipDetail.relationship"
                                control={form.control}
                                render={({ field, fieldState }) => (
                                  <OptionsSelector
                                    optionsAvailable={relationshipOptions}
                                    onChangeValueSelected={field.onChange}
                                    optionSelected={field.value}
                                    defaultText={t(
                                      'account-opening.legal-declarations.public-person-relationship.detail.relationship-placeholder',
                                      { ns: 'components' },
                                    )}
                                    selectType="white"
                                    dataTestId="public-person-relationship-selector"
                                    error={!!fieldState.error}
                                  />
                                )}
                              />
                            </fieldset>

                            <fieldset className="col-span-1 grid gap-2">
                              <Label
                                htmlFor="public-person-position-input"
                                required
                              >
                                <Translation
                                  i18nKey="account-opening.legal-declarations.public-person-relationship.detail.public-position"
                                  ns="components"
                                />
                              </Label>

                              <Controller
                                name="publicPersonRelationshipDetail.publicPosition"
                                control={form.control}
                                render={({ field, fieldState }) => (
                                  <Input
                                    id="public-person-position-input"
                                    value={field.value ?? ''}
                                    onChange={field.onChange}
                                    placeholder={t(
                                      'account-opening.legal-declarations.public-person-relationship.detail.public-position-placeholder',
                                      { ns: 'components' },
                                    )}
                                    data-testid="public-person-position-input"
                                    error={!!fieldState.error}
                                  />
                                )}
                              />
                            </fieldset>

                            <fieldset className="col-span-1 grid gap-2">
                              <Label
                                htmlFor="public-person-institution-input"
                                required
                              >
                                <Translation
                                  i18nKey="account-opening.legal-declarations.public-person-relationship.detail.institution"
                                  ns="components"
                                />
                              </Label>

                              <Controller
                                name="publicPersonRelationshipDetail.institution"
                                control={form.control}
                                render={({ field, fieldState }) => (
                                  <Input
                                    id="public-person-institution-input"
                                    value={field.value ?? ''}
                                    onChange={field.onChange}
                                    placeholder={t(
                                      'account-opening.legal-declarations.public-person-relationship.detail.institution-placeholder',
                                      { ns: 'components' },
                                    )}
                                    data-testid="public-person-institution-input"
                                    error={!!fieldState.error}
                                  />
                                )}
                              />
                            </fieldset>
                          </div>
                        </Card>
                      )}
                    </fieldset>
                  )}
                />
              </Card>

              <Card className="!rounded-xl !p-6" variant="outlined">
                <div className="flex flex-col gap-6">
                  <Typography
                    variant="h5"
                    className="!text-h5 !font-semibold !text-title-navy"
                  >
                    <Translation
                      i18nKey="account-opening.legal-declarations.tax-residence.title"
                      ns="components"
                    />
                  </Typography>

                  <fieldset className="col-span-1 grid gap-2">
                    <Label
                      component="legend"
                      required
                      className="mb-4 !text-base !font-normal !text-title-navy"
                    >
                      <Translation
                        i18nKey="account-opening.legal-declarations.tax-residence.us-question"
                        ns="components"
                      />
                    </Label>

                    <Controller
                      control={form.control}
                      name="isUsTaxResidence"
                      render={({ field }) => (
                        <div className="flex flex-col gap-3">
                          {yesNoOptions.map(option => {
                            const isSelected = field.value === option.value;

                            return (
                              <button
                                key={String(option.value)}
                                type="button"
                                onClick={() => onUsTaxResidenceChange(option.value)}
                                className={classNames(
                                  'flex w-full items-center gap-3 rounded-2xl border border-border-light bg-white px-4 py-3 text-left transition-all hover:border-primary hover:bg-primary/5',
                                  {
                                    'border-primary bg-primary/5 ring-1 ring-primary/20':
                                      isSelected,
                                  },
                                )}
                                data-testid={`us-tax-residence-option-${option.value}`}
                              >
                                <Radio
                                  checked={isSelected}
                                  className="!p-0 !text-primary hover:!bg-transparent"
                                />

                                <Typography
                                  variant="body1"
                                  className="!text-sm !font-normal !text-title-navy sm:!text-base"
                                >
                                  <Translation
                                    i18nKey={option.label as any}
                                    ns="components"
                                  />
                                </Typography>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    />
                  </fieldset>

                  {!legalDeclarations.isUsTaxResidence && (
                    <>
                      <fieldset className="col-span-1 grid gap-3">
                        <Label htmlFor="tax-residence-country-selector" required>
                          <Translation
                            i18nKey="account-opening.legal-declarations.tax-residence.country.label"
                            ns="components"
                          />
                        </Label>

                        <Controller
                          control={form.control}
                          name="taxResidenceCountry"
                          render={({ field }) => (
                            <OptionsSelector
                              optionsAvailable={taxResidenceCountryOptions}
                              onChangeValueSelected={field.onChange}
                              optionSelected={field.value}
                              defaultText={t(
                                'account-opening.personal-data.basic-info.nationality-placeholder',
                                { ns: 'components' },
                              )}
                              selectType="white"
                              searchBar
                              dataTestId="tax-residence-country-selector"
                              required
                            />
                          )}
                        />
                      </fieldset>

                      <fieldset className="col-span-1 grid gap-2">
                        <Label htmlFor="tax-residence-document-input" required>
                          <Translation
                            i18nKey="account-opening.legal-declarations.tax-residence.document-number"
                            ns="components"
                          />
                        </Label>

                        <Controller
                          control={form.control}
                          name="taxResidenceDocumentNumber"
                          render={({ field, fieldState }) => (
                            <Input
                              id="tax-residence-document-input"
                              value={field.value ?? ''}
                              onChange={event =>
                                onTaxResidenceDocumentNumberChange(
                                  event.target.value,
                                )
                              }
                              placeholder={t(
                                'account-opening.legal-declarations.tax-residence.document-number-placeholder',
                                { ns: 'components' },
                              )}
                              data-testid="tax-residence-document-input"
                              error={!!fieldState.error}
                            />
                          )}
                        />
                      </fieldset>
                    </>
                  )}

                  {legalDeclarations.isUsTaxResidence && (
                    <fieldset className="col-span-1 grid gap-2">
                      <Label htmlFor="us-tax-residence-document-input" required>
                        <Translation
                          i18nKey="account-opening.legal-declarations.tax-residence.us-document-number"
                          ns="components"
                        />
                      </Label>

                      <Controller
                        control={form.control}
                        name="taxResidenceDocumentNumber"
                        render={({ field, fieldState }) => (
                          <Input
                            id="us-tax-residence-document-input"
                            value={field.value ?? ''}
                            onChange={field.onChange}
                            placeholder={t(
                              'account-opening.legal-declarations.tax-residence.us-document-number-placeholder',
                              { ns: 'components' },
                            )}
                            data-testid="us-tax-residence-document-input"
                            error={!!fieldState.error}
                          />
                        )}
                      />
                    </fieldset>
                  )}
                </div>
              </Card>

              <Card className="!rounded-xl !p-6" variant="outlined">
                <Controller
                  control={form.control}
                  name="investmentOwnership"
                  render={({ field }) => (
                    <fieldset className="col-span-1 grid gap-2">
                      <Typography
                        variant="h5"
                        className="!mb-6 !text-h5 !font-semibold !text-title-navy"
                      >
                        <Translation
                          i18nKey="account-opening.legal-declarations.investment-ownership.title"
                          ns="components"
                        />
                      </Typography>

                      <div className="flex flex-col gap-3">
                        {investmentOwnershipOptions.map(option => {
                          const isSelected = field.value === option.value;

                          return (
                            <button
                              key={String(option.value)}
                              type="button"
                              onClick={() => field.onChange(option.value)}
                              className={classNames(
                                'flex w-full items-center gap-3 rounded-2xl border border-border-light bg-white px-4 py-3 text-left transition-all hover:border-primary hover:bg-primary/5',
                                {
                                  'border-primary bg-primary/5 ring-1 ring-primary/20':
                                    isSelected,
                                },
                              )}
                              data-testid={`investment-ownership-option-${option.value}`}
                            >
                              <Radio
                                checked={isSelected}
                                className="!p-0 !text-primary hover:!bg-transparent"
                              />

                              <Typography
                                variant="body1"
                                className="!text-sm !font-normal !text-title-navy sm:!text-base"
                              >
                                <Translation
                                  i18nKey={option.label as any}
                                  ns="components"
                                />
                              </Typography>
                            </button>
                          );
                        })}
                      </div>

                      {field.value === 'other-person' && (
                        <Card
                          className="!mt-5 !rounded-xl !bg-page !p-4"
                          variant="outlined"
                        >
                          <div className="flex flex-col gap-4">
                            <Typography
                              variant="h6"
                              className="!text-title-navy"
                              data-testid="beneficial-owner-detail-title"
                            >
                              <Translation
                                i18nKey="account-opening.legal-declarations.investment-ownership.detail.title"
                                ns="components"
                              />
                            </Typography>

                            <fieldset className="col-span-1 grid gap-2">
                              <Label
                                htmlFor="beneficial-owner-first-name-input"
                                required
                              >
                                <Translation
                                  i18nKey="account-opening.legal-declarations.investment-ownership.detail.first-name"
                                  ns="components"
                                />
                              </Label>

                              <Controller
                                name="beneficialOwnerDetail.firstName"
                                control={form.control}
                                render={({ field, fieldState }) => (
                                  <Input
                                    id="beneficial-owner-first-name-input"
                                    value={field.value ?? ''}
                                    onChange={field.onChange}
                                    placeholder={t(
                                      'account-opening.personal-data.basic-info.first-name-placeholder',
                                      { ns: 'components' },
                                    )}
                                    data-testid="beneficial-owner-first-name-input"
                                    error={!!fieldState.error}
                                  />
                                )}
                              />
                            </fieldset>

                            <fieldset className="col-span-1 grid gap-2">
                              <Label
                                htmlFor="beneficial-owner-last-name-input"
                                required
                              >
                                <Translation
                                  i18nKey="account-opening.legal-declarations.investment-ownership.detail.last-name"
                                  ns="components"
                                />
                              </Label>

                              <Controller
                                name="beneficialOwnerDetail.lastName"
                                control={form.control}
                                render={({ field, fieldState }) => (
                                  <Input
                                    id="beneficial-owner-last-name-input"
                                    value={field.value ?? ''}
                                    onChange={field.onChange}
                                    placeholder={t(
                                      'account-opening.personal-data.basic-info.first-surname-placeholder',
                                      { ns: 'components' },
                                    )}
                                    data-testid="beneficial-owner-last-name-input"
                                    error={!!fieldState.error}
                                  />
                                )}
                              />
                            </fieldset>

                            <fieldset className="col-span-1 grid gap-2">
                              <Label
                                htmlFor="beneficial-owner-document-input"
                                required
                              >
                                <Translation
                                  i18nKey="account-opening.legal-declarations.investment-ownership.detail.document-number"
                                  ns="components"
                                />
                              </Label>

                              <Controller
                                name="beneficialOwnerDetail.documentNumber"
                                control={form.control}
                                render={({ field, fieldState }) => (
                                  <Input
                                    id="beneficial-owner-document-input"
                                    value={field.value ?? ''}
                                    onChange={field.onChange}
                                    placeholder={t(
                                      'account-opening.personal-data.basic-info.document-number-placeholder',
                                      { ns: 'components' },
                                    )}
                                    data-testid="beneficial-owner-document-input"
                                    error={!!fieldState.error}
                                  />
                                )}
                              />
                            </fieldset>
                          </div>
                        </Card>
                      )}
                    </fieldset>
                  )}
                />
              </Card>
            </fieldset>
          </form>
        </div>
      </ScreenLayout>
    );
  },
});
